import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useAppContext } from "../../../context/AppContext";
import { ConnectionModel } from "../../../models/workFlow/ConnectionModel";
import {
  createConnection,
  testConnection,
  updateConnection,
} from "../../../services/AdminServices/ConnectionService";

interface ConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection?: ConnectionModel | null;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  connectionType: string;
  description: string;
  actionType: string;
  // API fields
  apiUrl: string;
  apiKey: string;
  authType: string;
  clientId: string;
  clientSecret: string;
  // Database fields
  dbServer: string;
  dbName: string;
  dbUsername: string;
  dbPassword: string;
  dbPort: number;
  dbType: string;
  swaggerEnable: boolean;
  swaggerUrl: string; // Add Swagger URL field
}

interface FormErrors {
  [key: string]: string;
}

const ConnectionModal: React.FC<ConnectionModalProps> = ({
  isOpen,
  onClose,
  connection,
  onSuccess,
}) => {
  const { accessToken, userData } = useAppContext();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showDbPassword, setShowDbPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [testLoading, setTestLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    connectionType: "",
    description: "",
    actionType: "", // Initialize ActionType
    apiUrl: "",
    apiKey: "",
    authType: "",
    clientId: "",
    clientSecret: "",
    dbServer: "",
    dbName: "",
    dbUsername: "",
    dbPassword: "",
    dbPort: 0,
    dbType: "",
    swaggerEnable: false,
    swaggerUrl: "", // Initialize Swagger URL
  });

  const isEditMode = Boolean(connection);

  // Initialize form data when modal opens or connection changes
  useEffect(() => {
    if (isOpen) {
      if (connection) {
        // Edit mode - populate form with existing data
        setFormData({
          name: connection.name || "",
          connectionType: connection.connectionType || "",
          description: connection.description || "",
          actionType: connection.actionType || "", // Handle existing ActionType
          apiUrl: connection.apiUrl || "",
          apiKey: connection.apiKey || "",
          authType: connection.authType || "",
          clientId: connection.clientId || "",
          clientSecret: connection.clientSecret || "",
          dbServer: connection.dbServer || "",
          dbName: connection.dbName || "",
          dbUsername: connection.dbUsername || "",
          dbPassword: connection.dbPassword || "",
          dbPort: connection.dbPort ? Number(connection.dbPort) : 0,
          dbType: connection.dbType || "",
          swaggerEnable: connection.isSwaggerEnabled,
          swaggerUrl: connection.swaggerUrl || "", // Initialize Swagger URL
        });
      } else {
        // Create mode - reset form
        setFormData({
          name: "",
          connectionType: "",
          description: "",
          actionType: "", // Reset ActionType
          apiUrl: "",
          apiKey: "",
          authType: "",
          clientId: "",
          clientSecret: "",
          dbServer: "",
          dbName: "",
          dbUsername: "",
          dbPassword: "",
          dbPort: 0,
          dbType: "",
          swaggerEnable: false,
          swaggerUrl: "", // Reset Swagger URL
        });
      }
      setErrors({});
    }
  }, [isOpen, connection]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Basic validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.connectionType) {
      newErrors.connectionType = "Connection type is required";
    }

    if (!formData.actionType) {
      newErrors.actionType = "Action type is required";
    }

    // Connection type specific validation
    if (formData.connectionType === "api") {
      if (!formData.apiUrl.trim()) {
        newErrors.apiUrl = "API URL is required";
      } else if (!isValidUrl(formData.apiUrl)) {
        newErrors.apiUrl = "Please enter a valid URL";
      }

      if (formData.authType === "apikey" && !formData.apiKey.trim()) {
        newErrors.apiKey = "API Key is required";
      }

      if (formData.authType === "oauth") {
        if (!formData.clientId.trim()) {
          newErrors.clientId = "Client ID is required";
        }
        if (!formData.clientSecret.trim()) {
          newErrors.clientSecret = "Client Secret is required";
        }
      }
    }

    if (formData.connectionType === "database") {
      if (!formData.dbServer.trim()) {
        newErrors.dbServer = "Database server is required";
      }
      if (!formData.dbName.trim()) {
        newErrors.dbName = "Database name is required";
      }
      if (!formData.dbUsername.trim()) {
        newErrors.dbUsername = "Database username is required";
      }
      if (!formData.dbPassword.trim()) {
        newErrors.dbPassword = "Database password is required";
      }
      if (!formData.dbType) {
        newErrors.dbType = "Database type is required";
      }
      if (formData.dbPort <= 0) {
        newErrors.dbPort = "Valid port number is required";
      }
    }

    if (formData.swaggerEnable && !formData.swaggerUrl.trim()) {
      newErrors.swaggerUrl = "Swagger URL is required";
    } else if (formData.swaggerEnable && !isValidUrl(formData.swaggerUrl)) {
      newErrors.swaggerUrl = "Please enter a valid Swagger URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!accessToken || !userData?.tenantID) {
      toast({
        title: "Error",
        description: "Authentication required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const connectionData: ConnectionModel = {
        connectionId: connection?.connectionId || 0,
        tenantId: userData.tenantID,
        name: formData.name,
        connectionType: formData.connectionType,
        description: formData.description,
        actionType: formData.actionType, // Include ActionType in the data
        // API fields
        apiUrl: formData.apiUrl,
        apiKey: formData.apiKey,
        authType: formData.authType,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        // Database fields
        dbServer: formData.dbServer,
        dbName: formData.dbName,
        dbUsername: formData.dbUsername,
        dbPassword: formData.dbPassword,
        dbPort: formData.dbPort > 0 ? String(formData.dbPort) : "",
        dbType: formData.dbType,
        // Audit fields
        createdBy: isEditMode
          ? connection?.createdBy || userData.userID
          : userData.userID,
        updatedBy: userData.userID,
        createdAt: connection?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isDeleted: false,
        isSwaggerEnabled: formData.swaggerEnable,
        swaggerUrl: formData.swaggerUrl, // Include Swagger URL
      };

      // Log the data being sent for debugging
      console.log(
        "Sending connection data:",
        JSON.stringify(connectionData, null, 2)
      );

      let result;
      if (isEditMode) {
        result = await updateConnection(accessToken, connectionData);
      } else {
        result = await createConnection(accessToken, connectionData);
      }

      if (result.success) {
        toast({
          title: "Success",
          description:
            result.message ||
            `Connection ${isEditMode ? "updated" : "created"} successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: "Error",
          description:
            result.message ||
            `Failed to ${isEditMode ? "update" : "create"} connection`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error saving connection:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      connectionType: "",
      description: "",
      actionType: "", // Reset ActionType
      apiUrl: "",
      apiKey: "",
      authType: "",
      clientId: "",
      clientSecret: "",
      dbServer: "",
      dbName: "",
      dbUsername: "",
      dbPassword: "",
      dbPort: 0,
      dbType: "",
      swaggerEnable: false,
      swaggerUrl: "", // Reset Swagger URL
    });
    setErrors({});
    onClose();
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the form errors before testing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!accessToken || !userData?.tenantID) {
      toast({
        title: "Error",
        description: "Authentication required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setTestLoading(true);

    try {
      const data: ConnectionModel = {
        connectionId: 0, // dummy value
        tenantId: userData.tenantID, // from logged-in user
        createdBy: userData.userID, // also from user
        isDeleted: false, // default value
        name: formData.name,
        connectionType: formData.connectionType,
        actionType: formData.actionType, // Include ActionType in test data

        // API fields
        apiUrl: formData.apiUrl,
        apiKey: formData.apiKey,
        authType: formData.authType,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,

        // DB fields
        dbServer: formData.dbServer,
        dbName: formData.dbName,
        dbUsername: formData.dbUsername,
        dbPassword: formData.dbPassword,
        dbPort: formData.dbPort > 0 ? String(formData.dbPort) : "",
        dbType: formData.dbType,
        isSwaggerEnabled: formData.swaggerEnable,
      };

      const result = await testConnection(accessToken, data);

      if (result.success) {
        toast({
          title: "Connection Test Successful",
          description: result.message || "Connection test passed",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Connection Test Failed",
          description: result.message || "Connection test failed",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      toast({
        title: "Test Connection Error",
        description:
          "An unexpected error occurred while testing the connection",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTestLoading(false);
    }
  };

  const renderBasicFields = () => (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={!!errors.name}>
        <FormLabel>Connection Name</FormLabel>
        <Input
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder="Enter connection name"
        />
        <FormErrorMessage>{errors.name}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.connectionType}>
        <FormLabel>Connection Type</FormLabel>
        <Select
          value={formData.connectionType}
          onChange={(e) => handleInputChange("connectionType", e.target.value)}
          placeholder="Select connection type"
        >
          <option value="api">API</option>
          <option value="database">Database</option>
          {/* <option value="file">File</option> */}
        </Select>
        <FormErrorMessage>{errors.connectionType}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.actionType}>
        <FormLabel>Action Type</FormLabel>
        <Select
          value={formData.actionType}
          onChange={(e) => handleInputChange("actionType", e.target.value)}
          placeholder="Select action type"
        >
          {/* <option value="UI Action">UI Action</option> */}
          <option value="UI Flow">UI Flow</option>
        </Select>
        <FormErrorMessage>{errors.actionType}</FormErrorMessage>
      </FormControl>

      <FormControl>
        <FormLabel>Description</FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Enter connection description"
          rows={3}
        />
      </FormControl>
    </VStack>
  );

  const renderApiFields = () => (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={!!errors.apiUrl}>
        <FormLabel>API URL</FormLabel>
        <Input
          value={formData.apiUrl}
          onChange={(e) => handleInputChange("apiUrl", e.target.value)}
          placeholder="https://api.example.com"
        />
        <FormErrorMessage>{errors.apiUrl}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.authType}>
        <FormLabel>Authentication Type</FormLabel>
        <Select
          value={formData.authType}
          onChange={(e) => handleInputChange("authType", e.target.value)}
          placeholder="Select authentication type"
        >
          <option value="none">None</option>
          <option value="apikey">API Key</option>
          <option value="oauth">OAuth</option>
          <option value="bearer">Bearer Token</option>
        </Select>
        <FormErrorMessage>{errors.authType}</FormErrorMessage>
      </FormControl>
      <FormControl>
        <FormLabel>Enable Swagger</FormLabel>
        <Checkbox
          isChecked={formData.swaggerEnable}
          onChange={(e) => handleInputChange("swaggerEnable", e.target.checked)}
        >
          Swagger Enabled
        </Checkbox>
      </FormControl>

      {formData.swaggerEnable && (
        <FormControl isRequired isInvalid={!!errors.swaggerUrl}>
          <FormLabel>Swagger URL</FormLabel>
          <Input
            value={formData.swaggerUrl}
            onChange={(e) => handleInputChange("swaggerUrl", e.target.value)}
            placeholder="Enter Swagger URL"
          />
          <FormErrorMessage>{errors.swaggerUrl}</FormErrorMessage>
        </FormControl>
      )}

      {(formData.authType === "apikey" || formData.authType === "bearer") && (
        <FormControl isRequired isInvalid={!!errors.apiKey}>
          <FormLabel>API Key</FormLabel>
          <InputGroup>
            <Input
              type={showApiKey ? "text" : "password"}
              value={formData.apiKey}
              onChange={(e) => handleInputChange("apiKey", e.target.value)}
              placeholder="Enter API key"
            />
            <InputRightElement>
              <IconButton
                aria-label={showApiKey ? "Hide API key" : "Show API key"}
                icon={showApiKey ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowApiKey(!showApiKey)}
                variant="ghost"
                size="sm"
              />
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
        </FormControl>
      )}

      {formData.authType === "oauth" && (
        <>
          <FormControl isRequired isInvalid={!!errors.clientId}>
            <FormLabel>Client ID</FormLabel>
            <Input
              value={formData.clientId}
              onChange={(e) => handleInputChange("clientId", e.target.value)}
              placeholder="Enter client ID"
            />
            <FormErrorMessage>{errors.clientId}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.clientSecret}>
            <FormLabel>Client Secret</FormLabel>
            <InputGroup>
              <Input
                type={showClientSecret ? "text" : "password"}
                value={formData.clientSecret}
                onChange={(e) =>
                  handleInputChange("clientSecret", e.target.value)
                }
                placeholder="Enter client secret"
              />
              <InputRightElement>
                <IconButton
                  aria-label={
                    showClientSecret
                      ? "Hide client secret"
                      : "Show client secret"
                  }
                  icon={showClientSecret ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.clientSecret}</FormErrorMessage>
          </FormControl>
        </>
      )}
    </VStack>
  );

  const renderDatabaseFields = () => (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired isInvalid={!!errors.dbType}>
        <FormLabel>Database Type</FormLabel>
        <Select
          value={formData.dbType}
          onChange={(e) => handleInputChange("dbType", e.target.value)}
          placeholder="Select database type"
        >
          <option value="mysql">MySQL</option>
          <option value="postgresql">PostgreSQL</option>
          <option value="sqlserver">SQL Server</option>
          <option value="oracle">Oracle</option>
          <option value="mongodb">MongoDB</option>
        </Select>
        <FormErrorMessage>{errors.dbType}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbServer}>
        <FormLabel>Database Server</FormLabel>
        <Input
          value={formData.dbServer}
          onChange={(e) => handleInputChange("dbServer", e.target.value)}
          placeholder="localhost or server IP"
        />
        <FormErrorMessage>{errors.dbServer}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbPort}>
        <FormLabel>Port</FormLabel>
        <NumberInput
          value={formData.dbPort}
          onChange={(_, value) => handleInputChange("dbPort", value || 0)}
          min={1}
          max={65535}
        >
          <NumberInputField placeholder="3306" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <FormErrorMessage>{errors.dbPort}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbName}>
        <FormLabel>Database Name</FormLabel>
        <Input
          value={formData.dbName}
          onChange={(e) => handleInputChange("dbName", e.target.value)}
          placeholder="Enter database name"
        />
        <FormErrorMessage>{errors.dbName}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbUsername}>
        <FormLabel>Username</FormLabel>
        <Input
          value={formData.dbUsername}
          onChange={(e) => handleInputChange("dbUsername", e.target.value)}
          placeholder="Enter database username"
        />
        <FormErrorMessage>{errors.dbUsername}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.dbPassword}>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <Input
            type={showDbPassword ? "text" : "password"}
            value={formData.dbPassword}
            onChange={(e) => handleInputChange("dbPassword", e.target.value)}
            placeholder="Enter database password"
          />
          <InputRightElement>
            <IconButton
              aria-label={showDbPassword ? "Hide password" : "Show password"}
              icon={showDbPassword ? <ViewOffIcon /> : <ViewIcon />}
              onClick={() => setShowDbPassword(!showDbPassword)}
              variant="ghost"
              size="sm"
            />
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{errors.dbPassword}</FormErrorMessage>
      </FormControl>
    </VStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text fontSize="xl" fontWeight="bold">
              {isEditMode ? "Edit Connection" : "Create New Connection"}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {isEditMode
                ? "Update connection details"
                : "Configure your new data connection"}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Basic Information */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4}>
                Basic Information
              </Text>
              {renderBasicFields()}
            </Box>

            {/* Connection-specific fields */}
            {formData.connectionType && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    {formData.connectionType === "api" && "API Configuration"}
                    {formData.connectionType === "database" &&
                      "Database Configuration"}
                    {formData.connectionType === "file" && "File Configuration"}
                  </Text>

                  {formData.connectionType === "api" && renderApiFields()}
                  {formData.connectionType === "database" &&
                    renderDatabaseFields()}
                  {formData.connectionType === "file" && (
                    <Alert status="info">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>File Connection</AlertTitle>
                        <AlertDescription>
                          File connection configuration will be available in the
                          next version.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={loading}
              isLoading={testLoading}
              loadingText="Testing..."
            >
              Test Connection
            </Button>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
              disabled={
                !formData.name ||
                !formData.connectionType ||
                !formData.actionType
              }
            >
              {isEditMode ? "Update Connection" : "Create Connection"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConnectionModal;
